import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Sparkles, X } from "lucide-react";
import api from "../lib/api";

// AchievementUnlockedPopup — shown ONCE per badge unlock.
//
// Trigger paths (both feed the SAME window-level queue):
//   1. Real-time earn: Timer / Chapter / Mock save handler receives
//      `newly_unlocked: [{id, emoji, title, tagline, ...}]` and calls
//      `enqueueBadgeUnlock(list)`.
//   2. Missed-popup recovery: AccessContext drains
//      `pending_badge_announcements` on session start via
//      POST /api/achievements/announcements/drain (READ-only) and
//      enqueues anything the user hasn't seen yet.
//
// Once-per-badge guarantee (survives refresh / logout / device change):
//   - Backend `_evaluate_and_persist_badges` writes new badges to
//     `users.pending_badge_announcements` via $addToSet.
//   - When the popup dismisses (auto-close OR manual click), we fire-and-
//     forget POST /api/achievements/celebration/mark-shown with the badge_id
//     which $pulls that entry from pending.
//   - Result: shown → cleared → never re-appears anywhere.
//
// Client-side dedupe:
//   - `SHOWN_IN_SESSION` ref tracks IDs already dequeued in THIS tab so a
//     redundant drain (from tab-visibility refresh, for instance) can't
//     double-show while the server confirmation is still in flight.
//
// Theme: premium black + emerald (matches Syllox brand). Emoji bounce +
// subtle emerald ring glow. Auto-dismisses at ~4.5s, honoring the spec's
// 3-5s window while giving users time to read the badge title.

const QUEUE_KEY = "__syllox_badge_queue__";
const VERSION_KEY = "__syllox_badge_queue_version__";
const AUTO_DISMISS_MS = 4500;

// Public API: any module can enqueue a badge unlock.
export function enqueueBadgeUnlock(badges) {
  if (!Array.isArray(badges) || badges.length === 0) return;
  // Dedupe against anything already queued OR already shown in this tab.
  const seen = new Set(
    (window[QUEUE_KEY] || []).map((b) => b.id).concat(
      Array.from(window.__syllox_badge_shown || new Set()),
    ),
  );
  const fresh = badges.filter((b) => b && b.id && !seen.has(b.id));
  if (fresh.length === 0) return;
  window[QUEUE_KEY] = (window[QUEUE_KEY] || []).concat(fresh);
  window[VERSION_KEY] = (window[VERSION_KEY] || 0) + 1;
  try { window.dispatchEvent(new Event("syllox:badge-queued")); } catch { /* ignore */ }
}

export default function AchievementUnlockedPopup() {
  const [current, setCurrent] = useState(null);
  const dismissTimer = useRef(null);

  // Per-tab record of badge IDs we've already surfaced. Prevents a
  // duplicate enqueue (e.g. AccessContext re-drains on tab focus while
  // mark-shown is still in flight) from re-showing the same badge.
  if (!window.__syllox_badge_shown) window.__syllox_badge_shown = new Set();

  // Drain the next badge from the queue
  const dequeueNext = () => {
    const q = window[QUEUE_KEY] || [];
    if (q.length === 0) return null;
    return q.shift();
  };

  const clearDismissTimer = () => {
    if (dismissTimer.current) {
      clearTimeout(dismissTimer.current);
      dismissTimer.current = null;
    }
  };

  const close = useCallback(() => {
    const badge = current;
    clearDismissTimer();
    setCurrent(null);
    if (badge?.id) {
      // Record locally IMMEDIATELY so a slow network can't re-open the same
      // popup during the animation window.
      window.__syllox_badge_shown.add(badge.id);
      // Fire-and-forget — server-side idempotency guarantees safety.
      api.post("/achievements/celebration/mark-shown", { badge_ids: [badge.id] })
        .catch(() => { /* swallow — non-critical */ });
    }
    // Chain to the next queued badge after the close animation
    setTimeout(() => {
      const next = dequeueNext();
      if (next) setCurrent(next);
    }, 350);
  }, [current]);

  useEffect(() => {
    const onQueued = () => {
      if (!current) {
        const next = dequeueNext();
        if (next) setCurrent(next);
      }
    };
    window.addEventListener("syllox:badge-queued", onQueued);
    // Drain anything queued before this component mounted
    onQueued();
    return () => window.removeEventListener("syllox:badge-queued", onQueued);
  }, [current]);

  // Start auto-dismiss whenever a new badge lands in `current`.
  useEffect(() => {
    if (!current) return;
    clearDismissTimer();
    dismissTimer.current = setTimeout(close, AUTO_DISMISS_MS);
    return clearDismissTimer;
  }, [current, close]);

  if (!current) return null;
  return (
    <Dialog open={!!current} onOpenChange={(o) => (o ? null : close())}>
      <DialogContent
        className="max-w-sm gap-0 overflow-hidden rounded-3xl border border-emerald-500/40 bg-zinc-950 p-0 text-white shadow-2xl shadow-emerald-500/30"
        data-testid="achievement-unlocked-popup"
        onEscapeKeyDown={close}
      >
        {/* Hero */}
        <div className="relative overflow-hidden bg-gradient-to-b from-black via-zinc-950 to-emerald-950/60 px-6 py-8 text-center">
          {/* Neon-green backlight glows */}
          <div className="pointer-events-none absolute -top-16 left-1/2 h-56 w-56 -translate-x-1/2 rounded-full bg-emerald-500/30 blur-3xl" aria-hidden="true"/>
          <div className="pointer-events-none absolute -right-10 -bottom-10 h-36 w-36 rounded-full bg-emerald-400/20 blur-2xl" aria-hidden="true"/>
          <div className="pointer-events-none absolute -left-10 -bottom-6 h-28 w-28 rounded-full bg-emerald-500/15 blur-2xl" aria-hidden="true"/>

          {/* Manual close */}
          <button
            type="button"
            onClick={close}
            aria-label="Close celebration"
            data-testid="achievement-close-x"
            className="absolute right-3 top-3 z-10 grid h-8 w-8 place-items-center rounded-full bg-white/10 text-white/80 backdrop-blur transition hover:bg-white/20 hover:text-white"
          >
            <X size={14}/>
          </button>

          <div className="relative flex flex-col items-center">
            {current.is_first_badge && (
              <div
                className="mb-3 rounded-full border border-emerald-400/60 bg-emerald-500/15 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-300 backdrop-blur"
                data-testid="achievement-first-badge-banner"
              >
                🎉 Your first achievement
              </div>
            )}
            <span
              className="relative text-6xl drop-shadow-[0_4px_18px_rgba(16,185,129,0.55)]"
              style={{ animation: "syllox-badge-bounce 700ms cubic-bezier(0.34, 1.56, 0.64, 1) both" }}
              aria-hidden="true"
              data-testid="achievement-badge-emoji"
            >
              {current.emoji}
            </span>
            <div className="mt-3 text-[10px] font-semibold uppercase tracking-[0.25em] text-emerald-300">
              🎉 Badge Unlocked
            </div>
            <DialogTitle className="mt-1 font-heading text-2xl font-bold text-white" data-testid="achievement-badge-title">
              {current.title}
            </DialogTitle>
          </div>
        </div>

        {/* Body */}
        <div className="border-t border-white/5 bg-zinc-950 px-6 py-5 text-center">
          <DialogDescription className="text-sm text-zinc-400" data-testid="achievement-badge-tagline">
            {current.tagline || "You earned a new Syllox achievement."}
          </DialogDescription>
          <Button
            type="button"
            onClick={close}
            data-testid="achievement-close"
            className="mt-5 w-full bg-gradient-to-r from-emerald-500 to-emerald-600 px-5 font-semibold text-black shadow-lg shadow-emerald-500/30 hover:from-emerald-400 hover:to-emerald-500"
          >
            <Sparkles size={14} className="mr-1.5"/>
            Awesome
          </Button>
        </div>

        {/* Scoped keyframes — badge bounce + a subtle ring pulse */}
        <style>{`
          @keyframes syllox-badge-bounce {
            0%   { transform: scale(0.55) rotate(-6deg); opacity: 0; }
            55%  { transform: scale(1.20) rotate(2deg);  opacity: 1; }
            100% { transform: scale(1.00) rotate(0deg);  opacity: 1; }
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
}
